import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  AdminNotificationPreferenceDTO,
  MasterAdminSettingsService,
} from '../tokens/master-admin-settings.token';
import { API_PATHS } from '../config/api-paths';
import { extractBackendErrorMessage } from '../util/backend-error';

/**
 * REST-only implementation of `MasterAdminSettingsService`.
 *
 * Calls:
 *   GET /services/embedded/api/master-admin/settings/preferences → AdminNotificationPreferenceDTO
 *   PUT /services/embedded/api/master-admin/settings/preferences → AdminNotificationPreferenceDTO
 *
 * Rule 07 / Rule 10 — no mock fallback. Playwright tests intercept via `page.route()`.
 * Rule 09 — updatePreferences surfaces backend error messages.
 *
 * Authority required: ROLE_MASTER_ADMIN (enforced server-side and by roleGuard on the route).
 */
@Injectable()
export class MasterAdminSettingsRestService implements MasterAdminSettingsService {
  private readonly http = inject(HttpClient);

  private get base(): string {
    return `${API_PATHS.EMBEDDED_API_BASE}/master-admin/settings/preferences`;
  }

  getPreferences(): Observable<AdminNotificationPreferenceDTO> {
    return this.http.get<AdminNotificationPreferenceDTO>(this.base);
  }

  updatePreferences(dto: AdminNotificationPreferenceDTO): Observable<AdminNotificationPreferenceDTO> {
    return this.http.put<AdminNotificationPreferenceDTO>(this.base, dto).pipe(
      catchError((err: HttpErrorResponse) => {
        const msg = extractBackendErrorMessage(err, 'errors.settings.saveFailed');
        return throwError(() => ({ code: msg }));
      }),
    );
  }
}
